/******/ (() => { // webpackBootstrap
/* eslint-disable */
if (!customElements.get('product-format-selector')) {
  const COLOR_OPTION_NAMES = ['color', 'colour', 'couleur', 'colore', 'farbe', '색', '色', 'färg', 'farve'];

  // Which format the shopper explicitly picked, per section - module-level so it
  // survives DOM replacements (the element is re-mounted on every colour change)
  const selectedFormatBySection = new Map();

  // Saved roll DOM state per section - module-level for the same reason
  const rollStateBySection = new Map();

  function getActiveColourValue(productInformation) {
    if (!productInformation) return null;

    const activeSwatch = productInformation.querySelector('.swatch-element.color.active');
    if (activeSwatch) return activeSwatch.getAttribute('data-value');

    const selectedDropdownItems = productInformation.querySelectorAll('li[data-product-variant][selected]');
    for (const item of selectedDropdownItems) {
      const optionName = item.getAttribute('data-option-name')?.trim().toLowerCase();
      if (COLOR_OPTION_NAMES.includes(optionName)) {
        return item.getAttribute('data-option-value');
      }
    }

    return null;
  }

  customElements.define('product-format-selector', class ProductFormatSelector extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onVariantUpdated = this.onVariantUpdated.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');
      this.sampleData = this.parseSampleData();
      this.addEventListener('change', this.onChange);
      eventBus.on('variant:updated', this.onVariantUpdated);
      this.enforceSelectedFormat();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      eventBus.off('variant:updated', this.onVariantUpdated);
    }

    get productInformation() {
      return document.querySelector(`product-information[data-section-id="${this.sectionId}"]`);
    }

    enforceSelectedFormat() {
      const selectedProductUrl = selectedFormatBySection.get(this.sectionId);
      if (!selectedProductUrl) return;

      const inputs = Array.from(this.querySelectorAll('input[data-product-url]'));
      const hasMatch = inputs.some(input => input.getAttribute('data-product-url') === selectedProductUrl);
      if (!hasMatch) return; // stale entry for a different product - trust the rendered markup instead

      inputs.forEach(input => {
        const isSelected = input.getAttribute('data-product-url') === selectedProductUrl;
        input.checked = isSelected;
        input.closest('.swatch-element')?.classList.toggle('active', isSelected);
      });
    }

    isInSampleFormat() {
      const selectedProductUrl = selectedFormatBySection.get(this.sectionId);
      if (!selectedProductUrl) return false;
      const sampleInput = this.querySelector('.swatch-element.sample input[data-product-url]');
      return sampleInput?.getAttribute('data-product-url') === selectedProductUrl;
    }

    onChange(event) {
      const input = event.target;
      if (!input || input.type !== 'radio') return;

      const productUrl = input.getAttribute('data-product-url');
      if (!productUrl) return;

      selectedFormatBySection.set(this.sectionId, productUrl);
      const isSample = input.closest('.swatch-element')?.classList.contains('sample');

      if (isSample) {
        this.saveRollState();
        this.applyVariantToDOM(this.findMatchingSampleVariant());
      } else {
        this.restoreRollState();
      }
    }

    onVariantUpdated(event) {
      if (event.sectionId !== this.sectionId) return;
      if (!this.isInSampleFormat()) return;

      // product-information has completed its full DOM replacement for a colour change.
      // Save the fresh roll state (new colour) then re-apply the matching sample variant.
      this.sampleData = this.parseSampleData();
      this.saveRollState();
      this.applyVariantToDOM(this.findMatchingSampleVariant());
    }

    parseSampleData() {
      const el = this.querySelector('script[data-sample-variants]');
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch { return null; }
    }

    findMatchingSampleVariant() {
      if (!this.sampleData?.variants?.length) return null;

      const { variants, colour_option_index: colIdx } = this.sampleData;

      if (colIdx != null) {
        const colourValue = getActiveColourValue(this.productInformation)?.trim().toLowerCase();
        if (colourValue) {
          const byColour = variants.filter(v => v[`option${colIdx + 1}`]?.trim().toLowerCase() === colourValue);
          const available = byColour.find(v => v.available);
          if (available) return available;
          if (byColour.length) return byColour[0];
        }
      }

      return variants.find(v => v.available) || variants[0] || null;
    }

    saveRollState() {
      const pi = this.productInformation;
      if (!pi) return;

      const priceEl = pi.querySelector('[data-product-price]');
      const compareEl = pi.querySelector('#ComparePrice, .compare-at-price');
      const skuEl = pi.querySelector('.current-variant--sku');
      const variantInput = pi.querySelector('input.product-variant-id');
      const atcBtn = pi.querySelector('button.product--add-to-cart-button[name="add"]');
      const oosEmailInput = pi.querySelector('out-of-stock-form input[name="contact[email]"]');
      const oosBlock = oosEmailInput?.closest('[data-update-id]');

      rollStateBySection.set(this.sectionId, {
        priceContent: priceEl?.getAttribute('content'),
        priceHtml: priceEl?.querySelector('[data-price-text]')?.textContent,
        compareVisible: compareEl?.classList.contains('on-sale'),
        compareHtml: compareEl?.querySelector('.money')?.textContent,
        sku: skuEl?.textContent,
        variantId: variantInput?.value,
        variantDisabled: variantInput?.disabled,
        atcDisabled: atcBtn?.disabled,
        atcText: atcBtn?.querySelector('[data-add-to-cart-text]')?.textContent,
        oosRequired: oosEmailInput?.hasAttribute('required'),
        oosHidden: oosBlock?.classList.contains('hidden'),
      });
    }

    restoreRollState() {
      const state = rollStateBySection.get(this.sectionId);
      if (!state) return;

      const pi = this.productInformation;
      if (!pi) return;

      const priceEl = pi.querySelector('[data-product-price]');
      const priceTextEl = priceEl?.querySelector('[data-price-text]');
      const compareEl = pi.querySelector('#ComparePrice, .compare-at-price');
      const compareMoneyEl = compareEl?.querySelector('.money');
      const skuEl = pi.querySelector('.current-variant--sku');
      const variantInput = pi.querySelector('input.product-variant-id');
      const atcBtn = pi.querySelector('button.product--add-to-cart-button[name="add"]');
      const atcTextEl = atcBtn?.querySelector('[data-add-to-cart-text]');
      const oosEmailInput = pi.querySelector('out-of-stock-form input[name="contact[email]"]');
      const oosBlock = oosEmailInput?.closest('[data-update-id]');

      if (priceEl && state.priceContent != null) priceEl.setAttribute('content', state.priceContent);
      if (priceTextEl && state.priceHtml != null) priceTextEl.textContent = state.priceHtml;
      if (compareEl) {
        compareEl.classList.toggle('on-sale', !!state.compareVisible);
        compareEl.classList.toggle('js', !state.compareVisible);
      }
      if (compareMoneyEl && state.compareHtml != null) compareMoneyEl.textContent = state.compareHtml;
      if (skuEl && state.sku != null) skuEl.textContent = state.sku;
      if (variantInput) {
        if (state.variantId != null) variantInput.value = state.variantId;
        variantInput.disabled = !!state.variantDisabled;
      }
      if (atcBtn) atcBtn.disabled = !!state.atcDisabled;
      if (atcTextEl && state.atcText != null) atcTextEl.textContent = state.atcText;
      if (oosEmailInput) oosEmailInput.required = !!state.oosRequired;
      if (oosBlock) oosBlock.classList.toggle('hidden', !!state.oosHidden);
    }

    applyVariantToDOM(variant) {
      if (!variant) return;

      const pi = this.productInformation;
      if (!pi) return;

      const priceEl = pi.querySelector('[data-product-price]');
      const priceTextEl = priceEl?.querySelector('[data-price-text]');
      const compareEl = pi.querySelector('#ComparePrice, .compare-at-price');
      const compareMoneyEl = compareEl?.querySelector('.money');
      const skuEl = pi.querySelector('.current-variant--sku');
      const variantInput = pi.querySelector('input.product-variant-id');
      const atcBtn = pi.querySelector('button.product--add-to-cart-button[name="add"]');
      const atcTextEl = atcBtn?.querySelector('[data-add-to-cart-text]');
      const oosEmailInput = pi.querySelector('out-of-stock-form input[name="contact[email]"]');
      const oosBlock = oosEmailInput?.closest('[data-update-id]');

      // The roll's "notify me" email field stays required in the DOM when swapping
      // to the sample format - clear it here so it doesn't block submitting an
      // in-stock sample just because the roll colour it was rendered for is sold out.
      if (oosEmailInput) oosEmailInput.required = !variant.available;
      if (oosBlock) oosBlock.classList.toggle('hidden', variant.available);

      if (priceEl) priceEl.setAttribute('content', Math.floor(variant.price / 100));
      if (priceTextEl) priceTextEl.textContent = variant.price_html;

      const hasCompareAt = variant.compare_at_price > 0;
      if (compareEl) {
        compareEl.classList.toggle('on-sale', hasCompareAt);
        compareEl.classList.toggle('js', !hasCompareAt);
        if (compareMoneyEl) compareMoneyEl.textContent = hasCompareAt ? (variant.compare_at_price_html || '') : '';
      }

      if (skuEl) skuEl.textContent = variant.sku || '';
      if (variantInput) {
        variantInput.value = variant.id;
        variantInput.disabled = !variant.available;
      }
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        if (atcTextEl) {
          atcTextEl.textContent = variant.available
            ? (this.getAttribute('data-add-to-cart-text') || 'Add to cart')
            : (this.getAttribute('data-sold-out-text') || 'Sold out');
        }
      }
    }
  });
}
/******/ })()
;
